import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import CartItems from './CartItems';
import checkoutAnalytics from './checkoutAnalytics';
import Price from '../Product/Price';
import Button from '../Elements/Button/Button';
import Checkbox from '../Elements/Checkbox/Checkbox';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import { purifier } from '../../utils/dom';
import { prefix, translate } from '../../utils/locale';
import { atob } from '../../utils/string';
import s from './Cart.scss';

const Cart = ({ checkout, setup, menuPopupHeight, toggleCartHandler, isOpen, position, productCache }) => {
	const { products = {} } = productCache?.[setup.market]?.[setup.pricelist] || {};
	const isPrivateConsumer = (!setup.consumerTypeActive || setup.consumerType === 'private');
	const isExVat = setup.countries?.[setup.country]?.exVAT;
	const [terms, updateTerms] = useState(!(isPrivateConsumer && isExVat));
	const [termsError, updateTermsError] = useState(false);
	const hasItems = checkout.selection.items && checkout.selection.items.length > 0;
	let subTotal = hasItems && checkout.selection.items.reduce((acc, i) => {
		let itemDetails = checkout.selectionCache.filter((scItem) => atob(scItem.selectionVariantId) === atob(i.id))[0];
		if (!itemDetails && products) itemDetails = products[i.cacheId];
		const price = i?.price?.[setup.currency]?.price || itemDetails?.price?.[setup.currency]?.price;

		return (acc + (parseInt(i.quantity, 10) * parseFloat(price)));
	}, 0);
	if (Number.isNaN(subTotal) || subTotal === 'NaN') subTotal = 0;

	let tradeLink;
	if (hasItems) tradeLink = `https://www.emeco.net/selection/link${checkout.selection.items.reduce((acc, i) => `${acc}/${i.id.slice(i.id.lastIndexOf('/') + 1)}x${i.quantity}`, '')}`;

	// Check if selectionCache has bundle items in checkout
	const bundleItemsInSelection = hasItems && checkout.selection.items.reduce((acc, item) => {
		let itemDetails = checkout.selectionCache.filter((scItem) => item.id === scItem.selectionVariantId)[0];
		if (!itemDetails && products) itemDetails = products[item.cacheId];
		if (!itemDetails?.bundles) return acc;
		const collectionId = itemDetails.collection;
		if (!acc[collectionId]) acc[collectionId] = item.quantity;
		else acc[collectionId] += item.quantity;
		return acc;
	}, {});

	const closestBundleItemSort = bundleItemsInSelection ? Object.keys(bundleItemsInSelection).sort((a, b) => (bundleItemsInSelection[a] < bundleItemsInSelection[b] ? 1 : -1)).map((i) => [i, 4 - bundleItemsInSelection[i]]) : [];
	const meetsBundleDiscountRequirements = bundleItemsInSelection && Object.keys(bundleItemsInSelection).filter((i) => bundleItemsInSelection[i] >= 4).length > 0;

	const goToCheckoutHandler = (e) => {
		if (!terms) {
			e.preventDefault();
			updateTermsError(true);
		}
		checkoutAnalytics(checkout.selection.items, setup, checkout.selection.totals);
	};

	const onCustomsAcceptHandler = (id, value) => {
		updateTerms(value);
		updateTermsError(false);
	};

	const dialogRef = useRef(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!isOpen || !dialog) return undefined;

		const previouslyFocused = document.activeElement;
		const focusableSelectors = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
		const getFocusable = () => Array.from(dialog.querySelectorAll(focusableSelectors));

		const firstFocusable = getFocusable()[0];
		if (firstFocusable) firstFocusable.focus();

		const handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				toggleCartHandler();
				return;
			}

			if (e.key === 'Tab') {
				const focusable = getFocusable();
				if (focusable.length === 0) {
					e.preventDefault();
					return;
				}
				const first = focusable[0];
				const last = focusable[focusable.length - 1];
				if (e.shiftKey) {
					if (document.activeElement === first) {
						e.preventDefault();
						last.focus();
					}
				} else if (document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			if (previouslyFocused) previouslyFocused.focus();
		};
	}, [isOpen, toggleCartHandler]);

	return (
		<div id="cart-drawer" ref={dialogRef} className={[s.cart, position === 'selection' ? s.cart__selection : null].join(' ')} style={{ height: `${menuPopupHeight}px` }} role="dialog" aria-modal="true" aria-label={translate(setup, 'cart')} aria-hidden={!isOpen}>
			<div className={s.cart_inner}>
				<div className={s.cart_inner_items}>
					<ErrorBoundary message="CartItems:" debugData={checkout.selection.items}>
						<CartItems toggleCartHandler={toggleCartHandler} products={products} />
					</ErrorBoundary>
				</div>
				{
					checkout.selection.items && checkout.selection.items.length > 0 && (
						<div className={[s.cart_inner_totals, checkout.selection.items && checkout.selection.items.length > 0 ? s.cart_inner_totals_hasItems : null].join(' ')}>
							<div className={[s.cart_inner_totals_row, s.cart_inner_totals_row__grandTotal].join(' ')} aria-live="polite" aria-atomic="true">
								<span className={s.cart_inner_totals_row_left}>{ translate(setup, 'total') }</span>
								<span className={s.cart_inner_totals_row_right}><Price price={{ price: { [setup.currency]: { price: subTotal } } }} cartTotal product setup={setup} /></span>
							</div>
							{
								isPrivateConsumer && isExVat && (
									<div className={s.cart_inner_totals_customs}>
										<Checkbox setup={setup} id={`customs-${position}`} selected={terms} inputChange={onCustomsAcceptHandler} isError={termsError}>
											<span dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutCustomsMessage')) }} />
										</Checkbox>
									</div>
								)
							}
							{
								bundleItemsInSelection && Object.keys(bundleItemsInSelection).length > 0 && (
									<div className={s.cart_inner_totals_bundles} aria-live="polite" aria-atomic="true">
										{
											meetsBundleDiscountRequirements ? (
												<div className={s.cart_inner_totals_bundle}>
													<span>{ translate(setup, 'checkoutBundleDiscountMet') }</span>
												</div>
											) : (
												<div className={s.cart_inner_totals_bundle}>
													<span>{ translate(setup, 'checkoutBundleDiscountAddMore').replace('%amount%', closestBundleItemSort?.[0]?.[1]).replace('%collection%', closestBundleItemSort?.[0]?.[0]) }</span>
												</div>
											)
										}
									</div>
								)
							}
							{
								parseInt(subTotal, 10) > 8e3 && tradeLink && (
									<div className={s.cart_inner_totals_trade}>
										<Link className={s.cart_inner_totals_trade_link} to={prefix(setup, translate(setup, 'checkoutTradeLink'))}>
											<span>{ translate(setup, 'checkoutTradeBadge') }</span>
											<span>{ translate(setup, 'checkoutTradeMessage') }</span>
										</Link>
									</div>
								)
							}
							{
								isPrivateConsumer && checkout.selection.webUrl && (
									<div className={s.cart_inner_totals_button}>
										<Button variantClasses={['button__purchase', termsError ? 'button__error' : null]} disabled={!terms} text={translate(setup, 'goToCheckout')} type="link" location={checkout.selection.webUrl} clickEventHandler={goToCheckoutHandler} />
										<div className={s.cart_inner_totals_button_terms} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutAcceptTerms'), { ALLOWED_TAGS: ['a'] }, setup) }} />
									</div>
								)
							}
							{
								!isPrivateConsumer && checkout.selection.webUrl && position === 'selection' && (
									<div className={s.cart_inner_totals_button}>
										<Button variantClasses={['button__purchase']} text={translate(setup, 'goToCheckout')} type="link" location={checkout.selection.webUrl} clickEventHandler={goToCheckoutHandler} />
										<div className={s.cart_inner_totals_button_terms} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutAcceptTerms'), { ALLOWED_TAGS: ['a'] }, setup) }} />
									</div>
								)
							}
							{
								!isPrivateConsumer && position !== 'selection' && (
									<div className={s.cart_inner_totals_button}>
										<Button variantClasses={['button__purchase']} text={translate(setup, 'goToCheckout')} type="link" location={prefix(setup, translate(setup, 'checkoutTradeLinkSelection'))} clickEventHandler={goToCheckoutHandler} />
									</div>
								)
							}
						</div>
					)
				}
			</div>
		</div>
	);
};

const mapState = ({ checkout, environment, productCache }, props) => {
	const { setup, header } = environment;
	return { checkout, setup, header, productCache, ...props };
};

export default hot(module)(connect(mapState)(Cart));
